import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'shikoba_auth_token',
    storage: localStorage,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    flowType: 'implicit'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-client-info': 'shikoba-app'
    }
  }
});

// Helper function to get profile by user ID
export async function getProfileById(userId: string) {
  try {
    if (!userId) {
      return { data: null, error: { message: 'User ID is required' } };
    }

    // Check if the user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('Auth error:', authError.message);
      return { data: null, error: authError };
    }
    
    if (!user) {
      console.error('No authenticated user found');
      return { 
        data: null, 
        error: { message: 'No authenticated user' }
      };
    }

    // Try to get existing profile
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        coach:coaches(*)
      `)
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Profile fetch error:', error.message);
      return { data: null, error };
    }
    
    // If profile exists, return it
    if (data) {
      return { data, error: null };
    }
    
    // If no profile exists, create a default one
    const defaultProfile = {
      id: userId,
      username: `user_${userId.slice(0, 8)}`,
      email: user.email || '',
      subscription_tier: 'free',
      role: 'client',
      difficulty_level: 'easy'
    };
    
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert([defaultProfile])
      .select()
      .single();
    
    if (insertError) {
      console.error('Profile creation error:', insertError.message);
      return { data: null, error: insertError };
    }
    
    return { data: newProfile, error: null };
  } catch (error: any) {
    console.error('Error in getProfileById:', error.message);
    return { 
      data: null, 
      error: {
        message: error.message || 'Failed to fetch or create profile',
        details: error
      }
    };
  }
}

// Helper function to update profile
export async function updateProfile(userId: string, updates: Partial<{
  username: string;
  first_name: string;
  last_name: string;
  subscription_tier: string;
  role: string;
  difficulty_level: string;
}>) {
  try {
    if (!userId) {
      return { data: null, error: { message: 'User ID is required' } };
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) {
      console.error('Profile update error:', error.message);
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (error: any) {
    console.error('Error in updateProfile:', error.message);
    return { data: null, error };
  }
}

// Helper function to get coach's clients
export async function getCoachClients(coachId: string) {
  try {
    const { data, error } = await supabase
      .from('coach_client_relationships')
      .select(`
        client:profiles!coach_client_relationships_client_id_fkey (
          id,
          username,
          first_name,
          last_name,
          email
        )
      `)
      .eq('coach_id', coachId)
      .eq('status', 'active');

    if (error) {
      console.error('Error fetching coach clients:', error);
      return { data: null, error };
    }

    return { 
      data: data.map(d => d.client),
      error: null 
    };
  } catch (error: any) {
    console.error('Error in getCoachClients:', error);
    return { 
      data: null,
      error: {
        message: error.message || 'Failed to fetch coach clients',
        details: error
      }
    };
  }
}

// Helper function to save journal entry
export async function saveJournalEntry(entry: any) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('No authenticated user');
    }

    if (entry._delete) {
      const { error } = await supabase
        .from('journal_entries')
        .delete()
        .eq('id', entry.id);
      
      return { data: null, error };
    }

    const entryData = {
      ...entry,
      id: entry.id || crypto.randomUUID(), // Generate UUID if not provided
      user_id: user.id,
      updated_at: new Date().toISOString()
    };

    let result;

    if (entry.id) {
      // Try to update existing entry
      result = await supabase
        .from('journal_entries')
        .update(entryData)
        .eq('id', entry.id)
        .eq('user_id', user.id)
        .select()
        .maybeSingle();

      // If no rows were updated (entry doesn't exist), create new entry
      if (!result.data && !result.error) {
        result = await supabase
          .from('journal_entries')
          .insert([entryData])
          .select()
          .single();
      }
    } else {
      // Create new entry
      result = await supabase
        .from('journal_entries')
        .insert([entryData])
        .select()
        .single();
    }

    if (result.error) throw result.error;
    return { data: result.data, error: null };
  } catch (error: any) {
    console.error('Error saving journal entry:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Failed to save journal entry',
        details: error
      }
    };
  }
}

// Helper function to fetch journal entry
export async function fetchJournalEntry(date: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', date)
      .maybeSingle();

    if (error) {
      // If the error is not related to no rows found, throw it
      if (error.code !== 'PGRST116') {
        throw error;
      }
      // For no rows found, return null data without error
      return { data: null, error: null };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Error fetching journal entry:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Failed to fetch journal entry',
        details: error
      }
    };
  }
}